import { useEffect, useState } from "react";
import { Plus, Recycle, Leaf, Wine, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function KegsPanel() {
  const { user } = useAuth();
  const [summary, setSummary] = useState({ rows: [], totals: {} });
  const [movements, setMovements] = useState([]);
  const [clients, setClients] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_name: "", type: "consegna", quantity: 1, deposit_per_keg: 30 });

  const load = async () => {
    const [s, m, c, al] = await Promise.all([api.get("/kegs/summary"), api.get("/kegs"), api.get("/clients"), api.get("/kegs/alerts")]);
    setSummary(s.data); setMovements(m.data); setClients(c.data); setAlerts(al.data.alerts || []);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const sendReminders = async () => {
    try { const { data } = await api.post("/kegs/send-reminders"); toast.success(`Promemoria: ${data.sent} inviati, ${data.skipped} saltati`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!form.client_name) { toast.error("Seleziona un cliente"); return; }
    try {
      await api.post("/kegs", { ...form, quantity: Number(form.quantity), deposit_per_keg: Number(form.deposit_per_keg) });
      toast.success("Movimento registrato"); setOpen(false);
      setForm({ client_name: "", type: "consegna", quantity: 1, deposit_per_keg: 30 });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const t = summary.totals || {};
  const cards = [
    { label: "Fusti consegnati", value: t.delivered ?? 0, icon: Wine, accent: "#0F4C81", bg: "#EFF6FF" },
    { label: "Fusti resi", value: t.returned ?? 0, icon: Recycle, accent: "#15803D", bg: "#DCFCE7" },
    { label: "In giro (da rientrare)", value: t.balance ?? 0, icon: Wine, accent: "#C2593F", bg: "#FFEDD5" },
    { label: "CO₂ risparmiata (kg)", value: t.co2_saved ?? 0, icon: Leaf, accent: "#15803D", bg: "#DCFCE7" },
  ];

  return (
    <div>
      {alerts.length > 0 && (
        <div className="mb-5 bg-red-50 border border-red-200 rounded-xl p-4" data-testid="keg-alerts">
          <div className="flex items-center gap-2 text-red-700 font-semibold mb-2"><AlertTriangle className="w-4 h-4" /> Fusti fermi da oltre 30 giorni</div>
          <div className="flex flex-wrap gap-2">
            {alerts.map((a) => (
              <span key={a.client_name} className="text-xs bg-white border border-red-200 rounded-full px-3 py-1" data-testid={`keg-alert-${a.client_name}`}>
                {a.client_name}: <b>{a.balance}</b> fusti · {a.days_out}gg
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl p-5 border border-stone-200" style={{ background: c.bg }} data-testid={`keg-stat-${c.label}`}>
            <c.icon className="w-5 h-5 mb-2" style={{ color: c.accent }} />
            <div className="text-xs text-stone-600">{c.label}</div>
            <div className="font-mono font-bold text-2xl" style={{ color: c.accent }}>{c.value}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-stone-500">
          Cauzioni in sospeso: <span className="font-mono font-semibold text-[#0B2A45]">{eur(t.deposit_out)}</span>
          {" · "}Bottiglie monouso evitate: <span className="font-semibold text-[#15803D]">{t.glass_saved ?? 0}</span>
        </div>
        <div className="flex items-center gap-2">
        {user?.role === "admin" && (
          <Button onClick={sendReminders} variant="outline" data-testid="send-keg-reminders-btn" className="rounded-full border-stone-300">
            <AlertTriangle className="w-4 h-4 mr-2" />Invia promemoria
          </Button>
        )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-keg-btn" className="bg-[#15803D] hover:bg-[#166534] text-white rounded-full">
              <Plus className="w-4 h-4 mr-2" />Registra movimento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Movimento fusti</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Cliente</Label>
                <Select value={form.client_name} onValueChange={(v) => setForm({ ...form, client_name: v })}>
                  <SelectTrigger className="mt-1" data-testid="keg-client-select"><SelectValue placeholder="Seleziona cliente" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo movimento</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1" data-testid="keg-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="consegna">Consegna (fusti in uscita)</SelectItem>
                    <SelectItem value="reso">Reso (fusti rientrati)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Quantità</Label><Input type="number" min="1" data-testid="keg-qty-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1" /></div>
                <div><Label>Cauzione/fusto €</Label><Input type="number" step="0.01" value={form.deposit_per_keg} onChange={(e) => setForm({ ...form, deposit_per_keg: e.target.value })} className="mt-1" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="save-keg-btn" className="bg-[#15803D] text-white rounded-full">Registra</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div>
          <h3 className="font-serif text-lg font-semibold text-[#0B2A45] mb-3">Bilancio per cliente</h3>
          <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Cliente</TableHead><TableHead>Consegn.</TableHead><TableHead>Resi</TableHead><TableHead>Saldo</TableHead><TableHead>Cauzione</TableHead></TableRow></TableHeader>
              <TableBody>
                {summary.rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-stone-400 py-6">Nessun movimento</TableCell></TableRow>}
                {summary.rows.map((r) => (
                  <TableRow key={r.client_name} data-testid={`keg-summary-${r.client_name}`}>
                    <TableCell className="font-medium">{r.client_name}</TableCell>
                    <TableCell className="font-mono">{r.delivered}</TableCell>
                    <TableCell className="font-mono">{r.returned}</TableCell>
                    <TableCell className="font-mono font-semibold text-[#C2593F]">{r.balance}</TableCell>
                    <TableCell className="font-mono">{eur(r.deposit_out)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        <div>
          <h3 className="font-serif text-lg font-semibold text-[#0B2A45] mb-3">Ultimi movimenti</h3>
          <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead>Tipo</TableHead><TableHead>Q.tà</TableHead></TableRow></TableHeader>
              <TableBody>
                {movements.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-stone-400 py-6">Nessun movimento</TableCell></TableRow>}
                {movements.slice(0, 15).map((m) => (
                  <TableRow key={m.id}>
                    <TableCell className="text-xs">{new Date(m.created_at).toLocaleDateString("it-IT")}</TableCell>
                    <TableCell>{m.client_name}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${m.type === "consegna" ? "bg-blue-100 text-blue-800" : "bg-green-100 text-green-800"}`}>{m.type}</span></TableCell>
                    <TableCell className="font-mono">{m.quantity}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
